import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { Link, useNavigate } from "react-router-dom";
import { resolveLogoSrc } from "../utils/resolveLogoSrc";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PaymentsIcon from "@mui/icons-material/Payments";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PrintIcon from "@mui/icons-material/Print";

function MonthlyReports() {
  const navigate = useNavigate();
  const shopLogoSrc =
    resolveLogoSrc(Helpers.authShop?.logo) ||
    require("../images/logo-light.png");

  const toMonthInput = (value = new Date()) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const [selectedMonth, setSelectedMonth] = useState(() => toMonthInput());
  const [report, setReport] = useState({ days: [], totals: {} });
  const [loading, setLoading] = useState(true);

  const formatCurrency = (value) =>
    `$${Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatMonthLabel = (value) => {
    if (!value || !/^\d{4}-\d{2}$/.test(value)) return "Monthly Report";
    const [year, month] = value.split("-").map((part) => parseInt(part, 10));
    return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  const formatDayLabel = (value) => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return value || "";
    const [year, month, day] = value.split("-").map((part) => parseInt(part, 10));
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const getReport = useCallback((month) => {
    setLoading(true);
    const tzOffsetMinutes = new Date().getTimezoneOffset();
    const query = `?month=${encodeURIComponent(month)}&tzOffsetMinutes=${tzOffsetMinutes}`;
    axios
      .get(
        `${Helpers.baseUrl}/reports/monthly/${Helpers.authParentId}${query}`,
        Helpers.headers,
      )
      .then((response) => {
        setReport({
          days: Array.isArray(response.data?.days) ? response.data.days : [],
          totals: response.data?.totals || {},
        });
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  useEffect(() => {
    getReport(selectedMonth);
  }, [getReport, selectedMonth]);

  const totals = useMemo(() => report.totals || {}, [report.totals]);
  const rows = Array.isArray(report.days) ? report.days : [];
  const generatedLabel = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const statCards = useMemo(
    () => [
      {
        title: "Business",
        value: formatCurrency(totals.business),
        icon: <AttachMoneyIcon sx={{ fontSize: 34 }} />,
        color: "#2563eb",
        bgColor: "rgba(37, 99, 235, 0.10)",
      },
      {
        title: "Collected Overall",
        value: formatCurrency(totals.collected),
        icon: <PaymentsIcon sx={{ fontSize: 34 }} />,
        color: "#059669",
        bgColor: "rgba(5, 150, 105, 0.10)",
      },
      {
        title: "Collected This Month",
        value: formatCurrency(totals.collectedFromBusiness),
        icon: <PaymentsIcon sx={{ fontSize: 34 }} />,
        color: "#0f766e",
        bgColor: "rgba(15, 118, 110, 0.10)",
      },
      {
        title: "Unpaid Overall",
        value: formatCurrency(totals.overallUnpaid),
        icon: <MoneyOffIcon sx={{ fontSize: 34 }} />,
        color: "#991b1b",
        bgColor: "rgba(153, 27, 27, 0.10)",
      },
      {
        title: "Unpaid This Month",
        value: formatCurrency(totals.unpaidFromBusiness),
        icon: <MoneyOffIcon sx={{ fontSize: 34 }} />,
        color: "#dc2626",
        bgColor: "rgba(220, 38, 38, 0.10)",
      },
      {
        title: "Tax",
        value: formatCurrency(totals.tax),
        icon: <ReceiptLongIcon sx={{ fontSize: 34 }} />,
        color: "#0f766e",
        bgColor: "rgba(15, 118, 110, 0.10)",
      },
      {
        title: "Card",
        value: formatCurrency(totals.card),
        icon: <CreditCardIcon sx={{ fontSize: 34 }} />,
        color: "#7c3aed",
        bgColor: "rgba(124, 58, 237, 0.10)",
      },
      {
        title: "Cash",
        value: formatCurrency(totals.cash),
        icon: <PointOfSaleIcon sx={{ fontSize: 34 }} />,
        color: "#d97706",
        bgColor: "rgba(217, 119, 6, 0.10)",
      },
        {
        title: "Check",
        value: formatCurrency(totals.check),
        icon: <RequestQuoteIcon sx={{ fontSize: 34 }} />,
        color: "#b45309",
        bgColor: "rgba(180, 83, 9, 0.10)",
      },
    ],
    [totals],
  );

  return (
    <>
    <Box
      className="screen-only"
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: 3,
        minHeight: "100vh",
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 3,
            borderRadius: 2,
            color: "white",
            background:
              "linear-gradient(135deg, #0f766e 0%, #0ea5e9 55%, #2563eb 100%)",
          }}
        >
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.75)" }} />}
            sx={{ mb: 2 }}
          >
            <MuiLink
              component={Link}
              to="/user/dashboard"
              underline="hover"
              sx={{ display: "flex", alignItems: "center", color: "rgba(255,255,255,0.9)" }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Home
            </MuiLink>
            <Typography sx={{ color: "white", fontWeight: 600 }}>
              Monthly Report
            </Typography>
          </Breadcrumbs>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                Monthly Report
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.9)" }}>
                Daily business, collections, unpaid balances, cash, card, and tax totals for {formatMonthLabel(selectedMonth)}
              </Typography>
            </Box>

            <Stack
              spacing={1.25}
              alignItems={{ xs: "stretch", md: "flex-end" }}
              sx={{
                width: { xs: "100%", sm: 200 },
                "@media print": { display: "none" },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
                <CalendarMonthIcon />
                <TextField
                  type="month"
                  size="small"
                  label="Month"
                  value={selectedMonth}
                  onChange={(event) => setSelectedMonth(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    flex: 1,
                    backgroundColor: "rgba(255,255,255,0.14)",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": { borderColor: "rgba(255,255,255,0.35)" },
                      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.55)" },
                      "&.Mui-focused fieldset": { borderColor: "white" },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.75)",
                    },
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
                <PrintIcon />
                <Button
                  variant="contained"
                  onClick={() => window.print()}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                    width: "100%",
                    backgroundColor: "rgba(255,255,255,0.18)",
                    color: "white",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.28)",
                      boxShadow: "none",
                    },
                  }}
                >
                  Print
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>

        <Box
          sx={{
            mb: 3,
            display: "grid",
            gap: 3,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
            alignItems: "stretch",
          }}
        >
          {statCards.map((card) => (
            <Card
              key={card.title}
              elevation={0}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
              }}
            >
              <CardContent sx={{ p: 2.5, height: "100%" }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 600 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: card.color }}>
                      {loading ? "..." : card.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: card.color,
                      backgroundColor: card.bgColor,
                    }}
                  >
                    {card.icon}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden", mb: 5.25 }}>
          <TableContainer sx={{ maxHeight: "calc(100vh - 360px)" }}>
            <Table
              stickyHeader
              sx={{
                tableLayout: "fixed",
                "& .month-day-column": {
                  width: { xs: 136, md: 148 },
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell className="month-day-column" sx={{ fontWeight: 700 }}>
                    Day
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Business</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Collected</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Unpaid</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Cash</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Card</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Check</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Tax</TableCell>
                  </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8, color: "text.secondary" }}>
                      No monthly data available.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.date} hover>
                      <TableCell className="month-day-column" sx={{ fontWeight: 600 }}>
                        {formatDayLabel(row.date)}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(row.business)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.collected)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.unpaid)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.cash)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.card)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.check)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.tax)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              <TableFooter>
                <TableRow
                  sx={{
                    "& td": {
                      fontWeight: 700,
                      backgroundColor: "#f8fafc",
                      borderTop: "2px solid #cbd5e1",
                  },
                }}
                >
                  <TableCell className="month-day-column">Total</TableCell>
                  <TableCell align="right">{formatCurrency(totals.business)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.collected)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.unpaid)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.cash)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.card)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.check)}</TableCell>
                  <TableCell align="right">{formatCurrency(totals.tax)}</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </Box>
      <Box className="print-only" sx={{ display: "none" }}>
        <div className="report-wrap">
          <div className="report-header">
            <div className="company-info">
              {shopLogoSrc && (
                <img
                  src={shopLogoSrc}
                  alt="Company Logo"
                  className="company-logo-print"
                />
              )}
              {!shopLogoSrc && Helpers.authShop?.name && (
                <h2 className="company-name">{Helpers.authShop.name}</h2>
              )}
            </div>
            <div className="report-title">MONTHLY REPORT</div>
          </div>

          <div className="report-meta">
            <div>
              <strong>Report Month:</strong> {formatMonthLabel(selectedMonth)}
            </div>
            <div>
              <strong>Generated:</strong> {generatedLabel}
            </div>
          </div>

          <div className="summary-stats monthly-summary-stats">
            {statCards.map((card) => (
              <div key={card.title} className="stat-box">
                <div className="stat-label">{card.title}</div>
                <div className="stat-value">{card.value}</div>
              </div>
            ))}
          </div>

          <div className="table-section">
            <div className="section-title-with-divider">
              <span className="section-title blue">Daily Breakdown</span>
            </div>
            <table className="report-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th style={{ textAlign: "right" }}>Business</th>
                  <th style={{ textAlign: "right" }}>Collected</th>
                  <th style={{ textAlign: "right" }}>Unpaid</th>
                  <th style={{ textAlign: "right" }}>Card</th>
                  <th style={{ textAlign: "right" }}>Cash</th>
                  <th style={{ textAlign: "right" }}>Check</th>
                  <th style={{ textAlign: "right" }}>Tax</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.date}>
                    <td>{formatDayLabel(row.date)}</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(row.business)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(row.collected)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(row.unpaid)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(row.card)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(row.cash)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(row.check)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(row.tax)}
                    </td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <strong>{formatCurrency(totals.business)}</strong>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <strong>{formatCurrency(totals.collected)}</strong>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <strong>{formatCurrency(totals.unpaid)}</strong>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <strong>{formatCurrency(totals.card)}</strong>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <strong>{formatCurrency(totals.cash)}</strong>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <strong>{formatCurrency(totals.check)}</strong>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <strong>{formatCurrency(totals.tax)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* <div className="table-section">
            <div className="section-title-with-divider">
              <span className="section-title blue">Summary Totals</span>
            </div>
            <table className="report-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Business</td>
                  <td style={{ textAlign: "right" }}>
                    {formatCurrency(totals.business)}
                  </td>
                </tr>
                <tr>
                  <td>Collected Overall</td>
                  <td style={{ textAlign: "right" }}>
                    {formatCurrency(totals.collected)}
                  </td>
                </tr>
                <tr>
                  <td>Collected This Month</td>
                  <td style={{ textAlign: "right" }}>
                    {formatCurrency(totals.collectedFromBusiness)}
                  </td>
                </tr>
                <tr>
                  <td>Unpaid Overall</td>
                  <td style={{ textAlign: "right" }}>
                    {formatCurrency(totals.overallUnpaid)}
                  </td>
                </tr>
                <tr>
                  <td>Unpaid This Month</td>
                  <td style={{ textAlign: "right" }}>
                    {formatCurrency(totals.unpaidFromBusiness)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div> */}

          <div className="report-footer">
            Generated by {Helpers.authShop?.name || "Auto Shop Management System"}
          </div>
        </div>
      </Box>

      <style>
        {`
          .print-only {
            display: none;
          }

          @media print {
            body {
              margin: 0;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .screen-only {
              display: none !important;
            }

            .print-only {
              display: block !important;
            }

            @page {
              size: A4;
              margin: 12mm;
            }

            .report-wrap {
              width: 100%;
              font-family: Arial, sans-serif;
              font-size: 11px;
              color: #000;
              line-height: 1.3;
            }

            .report-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 3px solid #2f5597;
              padding-bottom: 8px;
              margin-bottom: 12px;
            }

            .company-info {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
            }

            .company-logo-print {
              max-height: 50px;
              max-width: 180px;
              object-fit: contain;
            }

            .company-name {
              margin: 0;
              font-size: 20px;
              font-weight: bold;
              color: #2f5597;
            }

            .report-title {
              font-size: 24px;
              font-weight: bold;
              color: #2f5597;
            }

            .report-meta {
              display: flex;
              justify-content: space-between;
              margin-bottom: 10px;
              font-size: 10px;
            }

            .summary-stats {
              display: grid;
              gap: 6px;
              margin-bottom: 10px;
              padding: 6px 8px;
              background-color: #f8f9fa;
              border-radius: 4px;
            }

            .monthly-summary-stats {
              grid-template-columns: repeat(5, minmax(0, 1fr));
            }

            .stat-box {
              text-align: center;
              padding: 4px 2px;
            }

            .stat-label {
              font-size: 9px;
              color: #666;
              text-transform: uppercase;
              margin-bottom: 2px;
            }

            .stat-value {
              font-size: 13px;
              font-weight: bold;
              color: #2f5597;
              line-height: 1.2;
            }

            .section-title-with-divider {
              display: flex;
              align-items: center;
              margin-bottom: 8px;
              position: relative;
            }

            .section-title-with-divider::before,
            .section-title-with-divider::after {
              content: "";
              flex: 1;
              height: 2px;
              background: #2f5597;
            }

            .section-title-with-divider::before {
              margin-right: 15px;
            }

            .section-title-with-divider::after {
              margin-left: 15px;
            }

            .section-title.blue {
              background: #2f5597 !important;
              color: white !important;
              padding: 5px 15px;
              white-space: nowrap;
              font-size: 12px;
              font-weight: bold;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .table-section {
              margin-bottom: 12px;
            }

            .report-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
            }

            .report-table thead {
              background-color: #7a8181 !important;
              color: #000000 !important;
              font-weight: 600;
              text-transform: uppercase;
              border: 1px solid #000;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .report-table th {
              padding: 4px 6px;
              font-size: 9px;
              border: 1px solid #000;
            }

            .report-table td {
              padding: 4px 6px;
              border-bottom: 1px solid #ddd;
              vertical-align: top;
              line-height: 1.25;
            }

            .report-table .total-row {
              background-color: #e3f2fd !important;
              font-weight: bold;
              border-top: 2px solid #2f5597;
              border-bottom: 2px solid #2f5597;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .report-footer {
              margin-top: 12px;
              text-align: center;
              font-weight: bold;
              color: #2f5597;
              font-size: 10px;
              border-top: 2px solid #2f5597;
              padding-top: 8px;
            }
          }
        `}
      </style>
    </>
  );
}

export default MonthlyReports;
